import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/api';
// Capacitor import removed

const BASE_URL = 'https://scores.aprmode.com';
const fetchWithTimeout = (url, opts, ms = 15000) => {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { ...opts, signal: ctrl.signal }).finally(() => clearTimeout(id));
};

const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

export function AuthProvider({ children }) {
  const _urlParams = new URLSearchParams(window.location.search);
  const _suToken = _urlParams.get('impersonate');

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    if (_suToken) return _suToken;
    return localStorage.getItem('pwa_token');
  });
  const [isImpersonating, setIsImpersonating] = useState(!!_suToken);
  const [loading, setLoading] = useState(true);
  const [initFilters, setInitFilters] = React.useState(null);

  useEffect(() => {
    if (_suToken) {
      const url = new URL(window.location.href);
      url.searchParams.delete('impersonate');
      window.history.replaceState({}, '', url.toString());
    }
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    // 10 s timeout so a slow /api/init never deadlocks AuthProvider (which
    // blocks the whole app from rendering until session check resolves).
    fetchWithTimeout(BASE_URL + '/api/init', { headers }, 10000)
      .then(r => r.json())
      .then(d => {
        if (d.user) setUser(d.user);
        else if (token && !isImpersonating) { localStorage.removeItem('pwa_token'); setToken(null); }
        if (d.filters) setInitFilters(d.filters);
      })
      .catch(err => {
        // Timeout: backend is slow but token may still be valid -- do NOT log out.
        if (err && err.name === 'AbortError') return;
        if (token && !isImpersonating) { localStorage.removeItem('pwa_token'); setToken(null); }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const login = async (username, password) => {
    const res = await fetchWithTimeout(BASE_URL + '/api/auth/login', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed');
    localStorage.setItem('pwa_token', data.token);
    setToken(data.token); setUser(data.user); setIsImpersonating(false); return data;
  };

  const register = async (username, password, display_name) => {
    const res = await fetchWithTimeout(BASE_URL + '/api/auth/register', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, display_name })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Registration failed');
    localStorage.setItem('pwa_token', data.token);
    setToken(data.token); setUser(data.user); setIsImpersonating(false); return data;
  };

  const doLogout = () => {
    if (token && !isImpersonating) fetch(BASE_URL + '/api/auth/logout', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }).catch(() => {});
    localStorage.removeItem('pwa_token'); setToken(null); setUser(null); setIsImpersonating(false);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const d = await authFetch('/api/auth/me', token);
        if (d.user) setUser(d.user);
      } catch {}
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading...</span></div>;
  const settings = (user && user.app_settings) ? user.app_settings : {};
  return (
    <AuthContext.Provider value={{ user, token, settings, initFilters, isImpersonating, login, register, logout: doLogout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
